import React, { useState } from "react";
import { Calendar, Clock, Edit, MoreVertical, Bell } from "lucide-react";
import Footer from "../LandingPage/Components/Footer";
import CalendarIcon from "../../Images/calendar.svg";
import patient from "../../Images/Patients icon.svg";
import line from "../../Images/line.svg";
import Frame_blue from "../../Images/Frame_blue.svg";
import Frame_yellow from "../../Images/Frame_yellow.svg";
import Frame_virtual from "../../Images/Frame_virtual.svg";

export default function ProfileCard({ userData }) {
  const [imageError, setImageError] = useState(false);

  if (!userData) return null;

  const { user, dashboard } = userData;

  /* -------------------- TODAY'S SCHEDULE LOGIC -------------------- */

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todaySchedule = dashboard?.scheduled_programs_list?.find((item) => {
    const startDate = new Date(item.start_date);
    const endDate = new Date(item.end_date);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    return today >= startDate && today <= endDate;
  });

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get user initials
  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .trim()
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const userInitials = getInitials(user.full_name);
  const hasAvatar = user.avatar_url && !imageError;

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-800">My Profile</h1>
        <button className="flex items-center gap-2 rounded border border-[#4AACC5] px-6 py-2 text-[#4AACC5] hover:bg-teal-50 transition-colors">
          <Edit className="w-4 h-4" />
          <span className="font-medium">Edit</span>
        </button>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col">
        {/* Top Section */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-6">
            {/* Avatar with initials fallback */}
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200">
              {hasAvatar ? (
                <img
                  src={user.avatar_url}
                  alt={user.full_name}
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
              ) : (
                <div className={`w-full h-full bg-[#4AACC5] flex items-center justify-center text-white font-semibold text-xl`}>
                  {userInitials}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <div className="bg-[#4AACC5] rounded-lg px-3 py-1 inline-flex w-fit">
                <span className="text-white text-sm font-normal">
                  ID - {user.user_id}
                </span>
              </div>
              <h2 className="text-zinc-800 text-xl font-bold">
                {user.full_name}
              </h2>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3.5">
            <div
              className={`rounded-full px-4 py-1 ${
                user.is_active ? "bg-emerald-100" : "bg-gray-200"
              }`}
            >
              <span
                className={`${
                  user.is_active ? "text-lime-800" : "text-gray-600"
                } text-sm`}
              >
                {user.is_active ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="flex items-center gap-2.5">
              <button className="w-9 h-9 bg-white rounded-full border border-gray-200 hover:border-gray-300 flex items-center justify-center">
                <Bell className="w-4 h-4 text-red-600" />
              </button>
              <button className="w-9 h-9 bg-white rounded-full border border-gray-200 hover:border-gray-300 flex items-center justify-center">
                <MoreVertical className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex items-end gap-5 text-sm text-gray-700 justify-end">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            <span>
              Program:{" "}
              <span className="font-medium text-zinc-800">
                {todaySchedule?.course?.title || "No Program Today"}
              </span>
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            <span>
              Today Class:{" "}
              <span className="font-medium text-zinc-800">
                {todaySchedule
                  ? `${formatTime(todaySchedule.start_time)} - ${formatTime(
                      todaySchedule.end_time
                    )}`
                  : "No class scheduled today"}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Instructor + Video Section */}
      <div className="flex gap-6">
        {/* Instructor Info - VERTICAL STRAIGHT COLUMNS */}
        <div className="w-[457px] bg-white rounded-lg border p-6">
          <div className="grid grid-cols-2 gap-x-8">
            {/* Left Column - Straight Vertical */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-700 text-sm">Instructor Name</div>
                <div className="text-zinc-800 text-xs font-medium">
                  {user.full_name}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Email</div>
                <div className="text-zinc-700 text-xs font-medium break-words">
                  {user.email}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-xs">Specialization</div>
                <div className="text-zinc-700 text-sm font-medium">
                  {user.specialization}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Department</div>
                <div className="text-zinc-700 text-xs font-medium">
                  {user.department}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Experience</div>
                <div className="text-zinc-700 text-xs font-medium">
                  {user.years_of_experience} years
                </div>
              </div>
              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Alternate Number</div>
                <div className="text-zinc-700 text-xs font-medium">
                  {user?.alternate_phone || "-"}
                </div>
              </div>
            </div>

            {/* Right Column - Straight Vertical */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Role</div>
                <div className="text-zinc-700 text-xs font-medium">
                  {user.roles?.[0]?.role.name || "-"}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-700 text-sm">LinkedIn URL</div>
                <div className="text-cyan-700 underline text-xs break-all">
                  {user.linkedin_url || "-"}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Twitter URL</div>
                <div className="text-cyan-700 text-xs font-medium break-words">
                  {user?.twitter_url || "-"}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Instagram URL</div>
                <div className="text-cyan-700 text-xs font-medium break-words">
                  {user?.instagram_url || "-"}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Mobile Number</div>
                <div className="text-zinc-700 text-xs font-medium">
                  {user?.phone || "-"}
                </div>
              </div>

              <div className="flex flex-col gap-1 text-left">
                <div className="text-gray-800 text-sm">Address</div>
                <div className="text-zinc-700 text-xs font-medium break-words">
                  {user.address}, {user.city}, {user.state}, {user.country} -{" "}
                  {user.zip_code}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video + Bio */}
        <div className="w-[711px] bg-white rounded-lg border p-6 flex flex-col gap-6">
          <h2 className="text-zinc-800 text-xl font-bold">
            Introduction Video
          </h2>
          <div className="w-full h-80 rounded-[20px] border border-stone-300 flex items-center justify-center bg-gray-50">
            {user.biovideo_url ? (
              <video
                src={user.biovideo_url}
                controls
                className="w-full h-full rounded-[20px] object-cover"
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="flex flex-col items-center gap-4 text-gray-500">
                <svg
                  className="w-16 h-16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-base font-medium">No video available</p>
              </div>
            )}
          </div>

          <h2 className="text-zinc-800 text-xl font-bold">BIO / Experience</h2>
          <p className="text-neutral-700 text-base leading-8">{user.bio}</p>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {/* 1. On Going Programs */}
        <div className="h-36 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
          <div className="flex items-start gap-3">
            <img src={CalendarIcon} alt="" className="w-6 h-6" />
            <h3 className="text-gray-900 text-base font-semibold leading-tight">
              On Going Programs
            </h3>
          </div>

          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-gray-900">
              {dashboard.ongoing_programs}
            </span>

            <div className="w-28 h-14">
              <img
                src={Frame_blue}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* 2. Students */}
        <div className="h-36 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
          <div className="flex items-start gap-3">
            <img src={patient} alt="" className="w-6 h-6" />
            <h3 className="text-gray-900 text-base font-semibold leading-tight">
              Students
            </h3>
          </div>

          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-gray-900">
              {dashboard.students}
            </span>

            <div className="w-28 h-14">
              <img
                src={line}
                alt="line chart"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* 3. Virtual Class */}
        <div className="h-36 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
          <div className="flex items-start gap-3">
            <img src={Frame_virtual} alt="" className="w-6 h-6" />
            <h3 className="text-gray-900 text-base font-semibold leading-tight">
              Virtual Class
            </h3>
          </div>

          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-gray-900">
              {dashboard.virtual_classes.length}
            </span>

            <div className="w-28 h-14">
              <img
                src={Frame_yellow}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* 4. Regular / In-person Class */}
        <div className="h-36 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
          <div className="flex items-start gap-3">
            <img src={patient} alt="" className="w-6 h-6" />
            <h3 className="text-gray-900 text-base font-semibold leading-tight">
              Regular/In-person class
            </h3>
          </div>

          <div className="flex items-end justify-between">
            <span className="text-4xl font-bold text-gray-900">
              {dashboard.inperson_classes.length}
            </span>

            <div className="w-28 h-14">
              <img
                src={line}
                alt="trend chart"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}